import {
  type GenerationSourceInventory,
  type PageCompositionJson,
  type SourceRecord,
  generationSourceInventorySchema,
  pageCompositionJsonSchema,
} from '../domains/lp-generation/schema';
import { getSectionTemplateRegistry } from '../domains/lp-generation/registry/index';
import {
  buildSectionRuntimeState,
} from '../domains/lp-generation/composer';
import { siteDocumentSchema, type SiteDocument, type SiteSection } from '../domains/site/schema';
import { getSectionImageLabel, getSectionImageUrl, supportsSectionImage } from './section-images';

function sanitizeText(value?: string) {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function unique(values: string[]) {
  return [...new Set(values)];
}

function mergeSourceRecord(
  nextRecord: SourceRecord,
  previousRecord?: SourceRecord,
): SourceRecord {
  if (!previousRecord) {
    return nextRecord;
  }

  return {
    ...nextRecord,
    notes: unique([...previousRecord.notes, ...nextRecord.notes]),
    quality:
      nextRecord.quality === 'unknown'
        ? previousRecord.quality
        : nextRecord.quality,
    sourceRef: nextRecord.sourceRef ?? previousRecord.sourceRef,
    structuredData: nextRecord.structuredData ?? previousRecord.structuredData,
    verification:
      previousRecord.verification === 'verified'
        ? 'verified'
        : nextRecord.verification,
  };
}

function deriveOverallMaterialQuality(records: SourceRecord[]) {
  const qualities = records
    .filter((record) => record.availability === 'available')
    .map((record) => record.quality);

  if (qualities.includes('low')) {
    return 'low' as const;
  }

  if (qualities.includes('high')) {
    return 'high' as const;
  }

  if (qualities.includes('medium')) {
    return 'medium' as const;
  }

  return 'unknown' as const;
}

function buildSourceInventorySummary(records: SourceRecord[]) {
  return {
    hasVerifiedProof: records.some(
      (record) =>
        ['proof_metric', 'proof_case', 'certification'].includes(record.kind) &&
        record.availability === 'available' &&
        record.verification === 'verified',
    ),
    hasVerifiedReview: records.some(
      (record) =>
        record.kind === 'review' &&
        record.availability === 'available' &&
        record.verification === 'verified',
    ),
    hasVerifiedPricing: records.some(
      (record) =>
        record.kind === 'pricing' &&
        record.availability === 'available' &&
        record.verification === 'verified',
    ),
    hasVerifiedAccess: records.some(
      (record) =>
        record.kind === 'access' &&
        record.availability === 'available' &&
        record.verification === 'verified',
    ),
    hasUiCapture: records.some(
      (record) =>
        record.kind === 'ui_capture' &&
        record.availability === 'available',
    ),
    hasServicePhoto: records.some(
      (record) =>
        ['service_photo', 'location_photo', 'person_photo', 'team_photo'].includes(
          record.kind,
        ) && record.availability === 'available',
    ),
    overallMaterialQuality: deriveOverallMaterialQuality(records),
  };
}

type TitledBodyItem = {
  title: string;
  body: string;
};

function findAccessField(
  section: { items: TitledBodyItem[] },
  kind: 'address' | 'access',
) {
  const keywordMap = {
    access: ['アクセス', '最寄', '駅', '徒歩'],
    address: ['住所', '所在地'],
  } as const;

  const matched = section.items.find((item) =>
    keywordMap[kind].some((keyword) => item.title.includes(keyword)),
  );

  return sanitizeText(matched?.body);
}

function buildSourceRecordsFromSection(section: SiteSection): SourceRecord[] {
  if (supportsSectionImage(section)) {
    const imageUrl = getSectionImageUrl(section);
    if (imageUrl) {
      return [
        {
          id: `image-${section.id}`,
          kind: 'service_photo',
          label: getSectionImageLabel(section),
          availability: 'available',
          verification: 'user_claimed',
          quality: 'unknown',
          sourceRef: imageUrl,
          notes: ['site document image synced'],
        },
      ];
    }
  }

  switch (section.type) {
    case 'proof':
      return [
        {
          id: `proof-${section.id}`,
          kind: 'proof_metric',
          label: section.title,
          availability: 'available',
          verification: 'user_claimed',
          quality: 'medium',
          structuredData: {
            items: section.items.map((item) => ({
              label: item.label,
              value: item.value,
            })),
          },
          notes: ['site document proof section synced'],
        },
      ];
    case 'review':
      return [
        {
          id: `review-${section.id}`,
          kind: 'review',
          label: section.title,
          availability: 'available',
          verification: 'user_claimed',
          quality: 'medium',
          structuredData: {
            items: section.items.map((item) => ({
              comment: item.comment,
              ...(item.role ? { role: item.role } : {}),
              name: item.name,
            })),
          },
          notes: ['site document review section synced'],
        },
      ];
    case 'pricing':
      return [
        {
          id: `pricing-${section.id}`,
          kind: 'pricing',
          label: section.title,
          availability: 'available',
          verification: 'user_claimed',
          quality: 'medium',
          structuredData: {
            items: section.items.map((item) => ({
              ...(item.body ? { body: item.body } : {}),
              price: item.price,
              title: item.title,
            })),
          },
          notes: ['site document pricing section synced'],
        },
      ];
    case 'access': {
      const address =
        findAccessField(section, 'address') ??
        sanitizeText(section.items[0]?.body);
      const accessNote =
        findAccessField(section, 'access') ??
        sanitizeText(section.items[1]?.body);

      if (!address && !accessNote) {
        return [];
      }

      return [
        {
          id: `access-${section.id}`,
          kind: 'access',
          label: section.title,
          availability: 'available',
          verification: 'user_claimed',
          quality: 'medium',
          structuredData: {
            ...(accessNote ? { accessNote } : {}),
            ...(address ? { address } : {}),
            items: [],
          },
          notes: ['site document access section synced'],
        },
      ];
    }
    case 'offer': {
      const body = sanitizeText(
        section.items
          .map((item) => [item.title, item.body].filter(Boolean).join('：'))
          .join('\n'),
      );

      if (!body) {
        return [];
      }

      return [
        {
          id: `offer-${section.id}`,
          kind: 'offer',
          label: section.title,
          availability: 'available',
          verification: 'user_claimed',
          quality: 'medium',
          structuredData: {
            body,
            items: [],
          },
          notes: ['site document offer section synced'],
        },
      ];
    }
    default:
      return [];
  }
}

function findDocumentPage(document: SiteDocument, pageId: string) {
  return document.pages.find((page) => page.id === pageId);
}

function findDocumentSection(
  document: SiteDocument,
  pageId: string,
  sectionId: string,
) {
  return findDocumentPage(document, pageId)?.sections.find(
    (section) => section.id === sectionId,
  );
}

export function buildSourceInventoryFromDocument(
  document: SiteDocument,
  previousSourceInventory?: GenerationSourceInventory,
): GenerationSourceInventory {
  const previousById = new Map(
    (previousSourceInventory?.records ?? []).map((record) => [record.id, record] as const),
  );

  const generatedRecords = document.pages.flatMap((page) =>
    page.sections.flatMap((section) =>
      buildSourceRecordsFromSection(section).map((record) =>
        mergeSourceRecord(record, previousById.get(record.id)),
      ),
    ),
  );

  const generatedIds = new Set(generatedRecords.map((record) => record.id));
  const generatedKinds = new Set(generatedRecords.map((record) => record.kind));
  const preservedRecords = (previousSourceInventory?.records ?? []).filter(
    (record) =>
      !generatedIds.has(record.id) &&
      (!generatedKinds.has(record.kind) || record.verification === 'verified'),
  );
  const records = [...generatedRecords, ...preservedRecords];

  return generationSourceInventorySchema.parse({
    records,
    summary: buildSourceInventorySummary(records),
  });
}

export function syncPageCompositionWithSourceInventory(
  pageComposition: PageCompositionJson,
  sourceInventory: GenerationSourceInventory,
): PageCompositionJson {
  return pageCompositionJsonSchema.parse({
    ...pageComposition,
    sourceInventory,
    pages: pageComposition.pages.map((page) => ({
      ...page,
      sections: page.sections.map((section) => {
        const template = getSectionTemplateRegistry(section.templateId);
        if (!template) {
          return section;
        }

        const runtimeState = buildSectionRuntimeState(template, sourceInventory);

        return {
          ...section,
          generationStatus: runtimeState.generationStatus,
          sourceBindings: runtimeState.sourceBindings,
          sourcePolicy: runtimeState.sourcePolicy,
          systemFieldValues: runtimeState.systemFieldValues,
        };
      }),
    })),
  });
}

export function syncDocumentPipelineContext(
  document: SiteDocument,
): SiteDocument {
  const pipelineContext = document.metadata.pipelineContext;
  if (!pipelineContext) {
    return document;
  }

  const baseSourceInventory =
    pipelineContext.sourceInventory ?? pipelineContext.pageComposition?.sourceInventory;
  const sourceInventory = buildSourceInventoryFromDocument(
    document,
    baseSourceInventory,
  );
  const pageComposition = pipelineContext.pageComposition
    ? syncPageCompositionWithSourceInventory(
        pipelineContext.pageComposition,
        sourceInventory,
      )
    : undefined;

  return {
    ...document,
    metadata: {
      ...document.metadata,
      pipelineContext: {
        ...pipelineContext,
        pageComposition,
        sourceInventory,
      },
    },
  };
}

export function markSectionSourcesVerified(
  document: SiteDocument,
  pageId: string,
  sectionId: string,
  options?: {
    note?: string;
    sourceRef?: string;
  },
): SiteDocument {
  const syncedDocument = syncDocumentPipelineContext(document);
  const pipelineContext = syncedDocument.metadata.pipelineContext;
  if (!pipelineContext?.pageComposition || !pipelineContext.sourceInventory) {
    throw new Error('pipeline context が見つかりません。');
  }

  const section = findDocumentSection(syncedDocument, pageId, sectionId);
  if (!section) {
    throw new Error('確認対象のセクションが見つかりません。');
  }

  const generatedRecords = buildSourceRecordsFromSection(section);
  if (generatedRecords.length === 0) {
    throw new Error('確認対象のソースが見つかりません。');
  }

  const verifiedIds = new Set(generatedRecords.map((record) => record.id));
  const verifiedNote = sanitizeText(options?.note);
  const verifiedSourceRef = sanitizeText(options?.sourceRef);
  const nextRecords = pipelineContext.sourceInventory.records.map((record) =>
    verifiedIds.has(record.id)
      ? {
          ...record,
          notes: unique([
            ...record.notes,
            ...(verifiedNote ? [verifiedNote] : []),
            'editor verified',
          ]),
          sourceRef: verifiedSourceRef ?? record.sourceRef,
          verification: 'verified' as const,
        }
      : record,
  );
  const nextSourceInventory = generationSourceInventorySchema.parse({
    ...pipelineContext.sourceInventory,
    records: nextRecords,
    summary: buildSourceInventorySummary(nextRecords),
  });
  const nextPageComposition = syncPageCompositionWithSourceInventory(
    pipelineContext.pageComposition,
    nextSourceInventory,
  );

  return siteDocumentSchema.parse({
    ...syncedDocument,
    metadata: {
      ...syncedDocument.metadata,
      pipelineContext: {
        ...pipelineContext,
        pageComposition: nextPageComposition,
        sourceInventory: nextSourceInventory,
      },
    },
  });
}
